// Formulário para adicionar carteira confiável

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Pencil, Clipboard, Download, Upload, Wallet } from 'lucide-react';
import { useWallets } from '@/contexts/WalletContext';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import { useToast } from '@/hooks/useToast';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { validateWalletAddress, validateEmail } from '@/utils/validation';

const emptyForm = {
  name: '',
  address: '',
  network: 'ethereum',
  email: '',
  phone: '',
  tags: '',
};

/** Mapeia networkName ou chainId do QR de carteira para valor do formulário (evm/solana). */
function mapQRNetworkToForm(networkName, chainId) {
  const name = (networkName || '').toLowerCase();
  const id = (chainId || '').toString().toLowerCase();
  if (name.includes('solana')) return 'solana';
  // Qualquer outra coisa (Ethereum, Base, Arbitrum, Polygon, etc.) tratamos como EVM genérico
  return 'ethereum';
}

export default function AddWalletForm({ isOpen, onClose, onExport, onImport, wallet: walletToEdit, initialAddress, initialWalletData }) {
  const { t } = useTranslation();
  const { addWallet, updateWallet, exportWallets, importWallets } = useWallets();
  const { walletAddress } = useWalletConnection();
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(false);
  const fileInputRef = React.useRef(null);
  const networkHintRef = React.useRef(null);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [networkHintOpen, setNetworkHintOpen] = useState(false);

  const isEdit = Boolean(walletToEdit?.id);
  const isAddressLocked = isEdit || Boolean(initialAddress || initialWalletData?.address);

  useEffect(() => {
    if (!networkHintOpen) return;
    const onDocClick = (e) => {
      if (networkHintRef.current && !networkHintRef.current.contains(e.target)) {
        setNetworkHintOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [networkHintOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (walletToEdit) {
      setFormData({
        name: walletToEdit.name || '',
        address: walletToEdit.address || '',
        network: walletToEdit.network || 'ethereum',
        email: walletToEdit.email || '',
        phone: walletToEdit.phone || '',
        tags: Array.isArray(walletToEdit.tags) ? walletToEdit.tags.join(', ') : (walletToEdit.tags || ''),
      });
    } else if (initialWalletData?.address?.trim()) {
      const network = mapQRNetworkToForm(initialWalletData.networkName, initialWalletData.network);
      setFormData({
        name: initialWalletData.name || '',
        address: initialWalletData.address.trim(),
        network: network,
        email: initialWalletData.email || '',
        phone: initialWalletData.phone || '',
        tags: Array.isArray(initialWalletData.tags) ? initialWalletData.tags.join(', ') : (initialWalletData.tags || ''),
      });
    } else if (initialAddress?.trim()) {
      setFormData({
        ...emptyForm,
        address: initialAddress.trim(),
      });
    } else {
      setFormData(emptyForm);
    }
    setErrors({});
    setNetworkHintOpen(false);
  }, [isOpen, walletToEdit, initialAddress, initialWalletData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePasteAddress = async () => {
    try {
      const text = await navigator.clipboard?.readText();
      if (text?.trim()) setFormData(prev => ({ ...prev, address: text.trim() }));
      if (errors.address) setErrors(prev => ({ ...prev, address: '' }));
    } catch {
      showError(t('wallets.pasteError'));
    }
  };

  const handleUseConnectedWallet = () => {
    if (walletAddress) {
      setFormData(prev => ({ ...prev, address: walletAddress }));
      if (errors.address) setErrors(prev => ({ ...prev, address: '' }));
    }
  };

  const handleExportClick = () => {
    if (onExport) onExport();
    else if (exportWallets) {
      const data = exportWallets();
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `carteiras-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      success(t('wallets.exported'));
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !importWallets) return;
    try {
      const text = await file.text();
      const ok = await importWallets(text, true);
      if (ok) success(t('wallets.imported'));
      else showError(t('wallets.importError'));
    } catch (err) {
      showError(err?.message || t('wallets.importErrorGeneric'));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Nome
    if (!formData.name.trim()) {
      newErrors.name = t('wallets.nameRequired');
    }

    // Endereço
    if (!formData.address.trim()) {
      newErrors.address = t('wallets.addressRequired');
    } else if (!validateWalletAddress(formData.address)) {
      newErrors.address = t('wallets.addressInvalid');
    }

    // Rede
    if (!formData.network) {
      newErrors.network = t('wallets.networkRequired');
    }

    // Email (opcional mas validar se preenchido)
    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = t('wallets.emailInvalid');
    }

    // Telefone (opcional)
    if (formData.phone && formData.phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = t('wallets.phoneInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      // Processar tags
      const tags = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const walletData = {
        name: formData.name.trim(),
        address: formData.address.trim(),
        network: formData.network,
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        tags,
      };

      if (isEdit) {
        await updateWallet(walletToEdit.id, walletData);
        success(t('wallets.updated'));
      } else {
        await addWallet(walletData);
        success(t('wallets.added'));
      }

      setFormData(emptyForm);
      onClose();
    } catch (err) {
      console.error('Erro ao adicionar carteira:', err);
      showError(err.message || t('wallets.addError'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-2xl max-w-sm w-full border border-purple-500/20">
        {/* Formulário */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Nome + Export/Import */}
          <div className="flex items-end justify-between gap-2">
            <div className="flex-1 min-w-0">
              <Input
                label={t('wallets.walletNameLabel')}
                name="name"
                placeholder={t('wallets.walletNamePlaceholder')}
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
                disabled={loading}
              />
            </div>
          </div>

          {/* Endereço (bloqueado quando vem da transferência ou em edição) */}
          <div>
            <label className="block text-gray-300 mb-2 font-semibold text-sm">
              {t('wallets.addressLabel')}
            </label>
            <div className="flex items-center gap-2">
              <input
                name="address"
                placeholder={t('wallets.addressPlaceholder')}
                value={formData.address}
                onChange={handleChange}
                disabled={loading || isAddressLocked}
                readOnly={isAddressLocked}
                className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 min-w-0"
              />
            </div>
            {isAddressLocked && (
              <p className="text-gray-500 text-xs mt-1">{t('wallets.addressFixedHint')}</p>
            )}
            {errors.address && (
              <p className="text-red-400 text-sm mt-1">{errors.address}</p>
            )}
          </div>

          {/* Rede */}
          <div ref={networkHintRef} className="relative">
            <div className="flex items-center gap-1.5 mb-2">
              <label className="block text-sm font-medium text-gray-300">{t('wallets.network')} *</label>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setNetworkHintOpen((v) => !v); }}
                className="w-5 h-5 flex items-center justify-center rounded-full border border-gray-500 text-gray-400 hover:text-white hover:border-white text-[11px] transition shrink-0"
                aria-label={formData.network === 'solana' ? t('wallets.networkSolanaHint') : t('wallets.networkEvmHint')}
                aria-expanded={networkHintOpen}
              >
                ?
              </button>
            </div>
            {networkHintOpen && (
              <div className="absolute left-0 top-full mt-1 z-50 min-w-[200px] max-w-[320px] rounded-lg bg-gray-700 border border-gray-600 shadow-lg p-3 text-sm text-gray-300">
                {formData.network === 'solana'
                  ? t('wallets.networkSolanaHint')
                  : t('wallets.networkEvmHint')}
              </div>
            )}
            <select
              name="network"
              value={formData.network}
              onChange={handleChange}
              disabled={loading || isEdit}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500 transition disabled:opacity-50"
            >
              <option value="ethereum">{t('wallets.networkGroupEvm')}</option>
              <option value="solana">{t('wallets.networkGroupSolana')}</option>
            </select>
            {errors.network && <p className="text-red-400 text-sm mt-1">{errors.network}</p>}
          </div>

          <Input label={`${t('wallets.email')} (${t('common.optional')})`} name="email" type="email" placeholder="usuario@example.com" value={formData.email} onChange={handleChange} error={errors.email} disabled={loading} />
          <Input label={`${t('wallets.phoneLabel')} (${t('common.optional')})`} name="phone" placeholder="(11) 9 8765-4321" value={formData.phone} onChange={handleChange} error={errors.phone} disabled={loading} />

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('wallets.tagsLabel')}</label>
            <input type="text" name="tags" placeholder={t('wallets.tagsPlaceholder')} value={formData.tags} onChange={handleChange} disabled={loading} className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition disabled:opacity-50" />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={loading}
              className="flex-1"
            >
              {loading ? (isEdit ? t('wallets.saving') : t('wallets.adding')) : (isEdit ? t('wallets.save') : t('wallets.add'))}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
